// iPhone photos are often HEIC, which other browsers and the server cannot show. Before any file input reports
// its files to the app, HEIC/HEIF images are converted to JPEG in the browser (Safari can decode them).
// Works for every <input type="file"> at once: the original change event is held back, the converted files are
// put into the input and a new change event is sent.
const isHeic = (f: File) => /image\/hei[cf]/i.test(f.type) || /\.(heic|heif)$/i.test(f.name);
async function decode(f: File): Promise<CanvasImageSource & { width: number; height: number }> {
  try { return await createImageBitmap(f); } catch {}
  const url = URL.createObjectURL(f);
  try { const img = new Image(); img.decoding = 'async'; img.src = url; await img.decode(); return img; } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
}
export async function toJpeg(f: File): Promise<File> {
  const img = await decode(f), max = 4096, scale = Math.min(1, max / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas'); canvas.width = Math.max(1, Math.round(img.width * scale)); canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext('2d'); if (!ctx) throw Error('no canvas');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', 0.9)); if (!blob) throw Error('no jpeg');
  return new File([blob], f.name.replace(/\.(heic|heif)$/i, '') + '.jpg', { type: 'image/jpeg', lastModified: f.lastModified });
}
if (typeof document !== 'undefined') document.addEventListener('change', e => {
  const input = e.target as HTMLInputElement;
  if (!(input instanceof HTMLInputElement) || input.type !== 'file' || input.dataset.converted === '1' || !input.files?.length) return;
  const files = [...input.files]; if (!files.some(isHeic)) return;
  e.stopImmediatePropagation();
  void (async () => {
    const dt = new DataTransfer();
    for (const f of files) { let out = f; if (isHeic(f)) { try { out = await toJpeg(f); } catch (err) { console.warn('HEIC conversion failed', err); } } dt.items.add(out); }
    input.files = dt.files; input.dataset.converted = '1';
    try { input.dispatchEvent(new Event('change', { bubbles: true })); } finally { delete input.dataset.converted; }
  })();
}, true);
